import { LogOut, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/features/auth/auth-provider";
import { NotificationBell } from "@/features/notifications/notification-bell";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface AppTopbarProps {
  className?: string;
}

export function AppTopbar({ className }: AppTopbarProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const roleLabel = user?.role.replaceAll("_", " ") ?? "";

  return (
    <header
      className={cn(
        "sticky top-0 z-40 flex h-14 items-center justify-between gap-4 border-b border-border/60 bg-card-white/95 px-4 backdrop-blur-sm md:px-6",
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="relative hidden max-w-sm flex-1 sm:block">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            placeholder="Search sites, workers, equipment…"
            className="h-9 w-full rounded-lg border border-input bg-bg-light pl-9 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/30"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <NotificationBell />

        {user ? (
          <div className="hidden items-center gap-2 border-l border-border pl-3 sm:flex">
            <div className="text-right">
              <p className="text-sm font-medium text-navy-deep">{user.name}</p>
              <p className="text-xs capitalize text-muted-foreground">{roleLabel}</p>
            </div>
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-navy-blueprint text-xs font-bold text-white">
              {user.name.charAt(0).toUpperCase()}
            </div>
          </div>
        ) : null}

        <Button
          variant="ghost"
          size="sm"
          onClick={() => void handleLogout()}
          className="text-muted-foreground"
        >
          <LogOut className="h-4 w-4 sm:mr-1" />
          <span className="hidden sm:inline">Sign out</span>
        </Button>
      </div>
    </header>
  );
}
